// Property list page — owner's main "find a listing" surface.
//
// Layout:
//
//   header row    Bất động sản N | stats         [search] [grid/table] [Thêm BĐS]
//   filter row    [active chips with × …]                   [Bộ lọc nâng cao]
//   results       <grid | table>
//   pagination    page controls
//
// URL is the source of truth for filters, search text, view choice,
// and AI-applied flag — see utils/property-filter-params.ts. Active
// filter chips render only when filters are applied (the row collapses
// to just the advanced-filter trigger otherwise).

import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Building, Building2, ChevronLeft, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/empty-state";
import { isStale } from "@/lib/freshness";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/lib/auth-store";
import { useMediaQuery } from "@/features/lead/hooks/use-media-query";
import { PaginationControls } from "@/features/property/components/pagination-controls";
import { PropertyCard } from "@/features/property/components/property-card";
import {
  ProjectCard,
  formatProjectPriceRange,
} from "@/features/property/components/project-card";
import { PropertyFilters } from "@/features/property/components/property-filters";
import { PropertyGroupToggle } from "@/features/property/components/property-group-toggle";
import { PropertyListHeader } from "@/features/property/components/property-list-header";
import { PropertyPageSizeSelector } from "@/features/property/components/property-page-size-selector";
import { PropertyTable } from "@/features/property/components/property-table";
import { useProperties } from "@/features/property/api";
import { useProject, useProjects, type Project } from "@/features/project/api";
import { ProjectFormDialog } from "@/features/project/components/project-form-dialog";
import { useOrganization } from "@/features/settings/api";
import {
  readFilterDraft,
  readPageSize,
  readView,
  type PropertyView,
} from "@/features/property/utils/property-filter-params";
import type { Property, PropertyListQuery } from "@/types/property";

const DEFAULT_STALE_DAYS = 14;

export default function PropertyListPage() {
  const [searchParams] = useSearchParams();

  // Only Owner / Manager / Source can create or edit projects (mirrors the
  // backend authz on POST/PATCH /projects).
  const role = useAuthStore((s) => s.user?.role);
  const canManageProjects =
    role === "OWNER" || role === "MANAGER" || role === "SOURCE";
  const [projectDialog, setProjectDialog] = useState<{
    open: boolean;
    project: Project | undefined;
  }>({ open: false, project: undefined });

  // Honor `?view=table` on desktop only — phones don't have room for the
  // 10-column table. On mobile we always render the card grid regardless
  // of the URL param (which we leave intact, so a desktop-shared link
  // still resolves to "table" when opened back on a wider screen).
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const requestedView = readView(searchParams);
  const view = isDesktop ? requestedView : "grid";

  const filterDraft = useMemo(
    () => readFilterDraft(searchParams),
    [searchParams],
  );
  const staleOnly = searchParams.get("stale") === "true";

  // Grouping: `?group=project` shows the projects; `?projectId=N` is a
  // drill-down into one project's units (which turns grouping off).
  const projectIdParam = searchParams.get("projectId");
  const projectId =
    projectIdParam !== null &&
    projectIdParam !== "" &&
    Number.isFinite(Number(projectIdParam))
      ? Number(projectIdParam)
      : undefined;
  const groupByProject =
    searchParams.get("group") === "project" && projectId === undefined;

  // The backend list endpoint only honors a subset of filter fields
  // today (status / wardCode / search). The rest are applied client-side
  // below until the backend ships them.
  const pageSize = readPageSize(searchParams);
  const listQuery = useMemo<PropertyListQuery>(() => {
    const rawPage = Number.parseInt(searchParams.get("page") ?? "0", 10);
    const page = Number.isNaN(rawPage) || rawPage < 0 ? 0 : rawPage;
    const base: PropertyListQuery = { page, size: pageSize };
    if (filterDraft.status !== undefined) base.status = filterDraft.status;
    if (filterDraft.wardCode !== undefined) base.wardCode = filterDraft.wardCode;
    if (filterDraft.search !== undefined) base.search = filterDraft.search;
    if (projectId !== undefined) base.projectId = projectId;
    return base;
  }, [searchParams, filterDraft, pageSize, projectId]);

  const propertiesQuery = useProperties(listQuery);

  // Project aggregation — only fetched in grouped mode (status/ward/search
  // honoured; the client-side-only filters don't apply to grouping).
  const projectQuery = useMemo<PropertyListQuery>(() => {
    const q: PropertyListQuery = {};
    if (filterDraft.status !== undefined) q.status = filterDraft.status;
    if (filterDraft.listingType !== undefined) q.listingType = filterDraft.listingType;
    if (filterDraft.wardCode !== undefined) q.wardCode = filterDraft.wardCode;
    if (filterDraft.search !== undefined) q.search = filterDraft.search;
    return q;
  }, [filterDraft]);
  const projectsQuery = useProjects(projectQuery, groupByProject);

  const orgQuery = useOrganization();
  const staleDays = orgQuery.data?.freshnessStaleDays ?? DEFAULT_STALE_DAYS;

  const visibleItems = useMemo(
    () =>
      filterClientSide(
        propertiesQuery.data?.items ?? [],
        filterDraft,
        staleOnly,
        staleDays,
      ),
    [propertiesQuery.data, filterDraft, staleOnly, staleDays],
  );

  const total = propertiesQuery.data?.totalItems ?? 0;
  const stats = useMemo(
    () => buildStatusStats(propertiesQuery.data?.items ?? []),
    [propertiesQuery.data],
  );
  const statsLine = formatStatsLine(stats);

  return (
    <div className="flex flex-col gap-4 px-6 py-5 md:gap-5">
      <PropertyListHeader total={total} statsLine={statsLine} />

      <PropertyFilters />

      {projectId !== undefined ? (
        <ProjectDrillBar projectId={projectId} />
      ) : (
        <div className="flex items-center justify-between gap-2">
          <PropertyGroupToggle />
          {groupByProject && canManageProjects ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setProjectDialog({ open: true, project: undefined })}
            >
              <Plus aria-hidden="true" className="size-4" />
              Thêm dự án
            </Button>
          ) : null}
        </div>
      )}

      {groupByProject ? (
        <ProjectsResults
          query={projectsQuery}
          view={view}
          onEditProject={
            canManageProjects
              ? (p) => setProjectDialog({ open: true, project: p })
              : undefined
          }
        />
      ) : (
        <>
          {propertiesQuery.isPending ? (
            view === "table" ? (
              <PropertyTableSkeleton />
            ) : (
              <PropertyGridSkeleton />
            )
          ) : propertiesQuery.isError ? (
            <ErrorState onRetry={() => propertiesQuery.refetch()} />
          ) : visibleItems.length === 0 ? (
            <EmptyState
              icon={<Building aria-hidden="true" className="size-10" />}
              title="Không tìm thấy bất động sản nào"
              description="Thử bỏ bớt bộ lọc hoặc kiểm tra lại từ khóa tìm kiếm."
            />
          ) : view === "table" ? (
            <PropertyTable items={visibleItems} staleDays={staleDays} />
          ) : (
            <PropertyGrid items={visibleItems} />
          )}

          {propertiesQuery.data ? (
            <footer className="flex flex-col items-center gap-3 sm:flex-row sm:items-center sm:justify-between">
              <PaginationControls
                page={propertiesQuery.data.page ?? 0}
                totalPages={propertiesQuery.data.totalPages ?? 0}
                totalItems={propertiesQuery.data.totalItems ?? 0}
              />
              <PropertyPageSizeSelector />
            </footer>
          ) : null}
        </>
      )}

      <ProjectFormDialog
        open={projectDialog.open}
        onOpenChange={(open) => setProjectDialog((prev) => ({ ...prev, open }))}
        project={projectDialog.project}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Projects (grouped-by-building) view
// ---------------------------------------------------------------------------

function ProjectsResults({
  query,
  view,
  onEditProject,
}: {
  query: ReturnType<typeof useProjects>;
  view: PropertyView;
  onEditProject?: ((project: Project) => void) | undefined;
}) {
  if (query.isPending) {
    return view === "table" ? <PropertyTableSkeleton /> : <PropertyGridSkeleton />;
  }
  if (query.isError) {
    return <ErrorState onRetry={() => query.refetch()} />;
  }
  const projects = query.data ?? [];
  if (projects.length === 0) {
    return (
      <EmptyState
        icon={<Building aria-hidden="true" className="size-10" />}
        title="Chưa có dự án nào"
        description="Thêm bất động sản có tên tòa nhà để nhóm chúng thành dự án."
      />
    );
  }
  return view === "table" ? (
    <ProjectTable projects={projects} />
  ) : (
    <ProjectGrid projects={projects} onEditProject={onEditProject} />
  );
}

function ProjectGrid({
  projects,
  onEditProject,
}: {
  projects: readonly Project[];
  onEditProject?: ((project: Project) => void) | undefined;
}) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {projects.map((p) => (
        <ProjectCard
          key={p.id}
          project={p}
          onEdit={onEditProject ? () => onEditProject(p) : undefined}
        />
      ))}
    </div>
  );
}

function ProjectTable({ projects }: { projects: readonly Project[] }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const drill = (id: number) => {
    const out = new URLSearchParams(searchParams);
    out.set("projectId", String(id));
    out.delete("group");
    out.delete("building");
    out.delete("page");
    navigate({ pathname: "/properties", search: `?${out.toString()}` });
  };
  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-[var(--shadow-soft)]">
      <table className="w-full border-collapse text-[14px]">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left text-xs tracking-wide text-muted-foreground uppercase">
            <th className="px-3 py-2 font-medium">Dự án</th>
            <th className="px-3 py-2 text-right font-medium">Số căn</th>
            <th className="px-3 py-2 text-right font-medium">Bán</th>
            <th className="px-3 py-2 text-right font-medium">Cho thuê</th>
            <th className="px-3 py-2 text-right font-medium">Còn trống</th>
            <th className="px-3 py-2 font-medium">Khoảng giá</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((p) => (
            <tr
              key={p.id}
              role="button"
              tabIndex={0}
              aria-label={`${p.name} — ${p.unitCount} căn`}
              onClick={() => drill(p.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  drill(p.id);
                }
              }}
              className={cn(
                "border-b border-border last:border-0",
                "cursor-pointer hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none",
              )}
            >
              <td className="px-3 py-2.5 font-medium">{p.name}</td>
              <td className="px-3 py-2.5 text-right font-mono tabular-nums">{p.unitCount}</td>
              <td className="px-3 py-2.5 text-right font-mono tabular-nums text-muted-foreground">{p.saleCount}</td>
              <td className="px-3 py-2.5 text-right font-mono tabular-nums text-muted-foreground">{p.rentCount}</td>
              <td className="px-3 py-2.5 text-right font-mono tabular-nums text-success">{p.availableCount}</td>
              <td className="px-3 py-2.5 font-mono text-[12px]">{formatProjectPriceRange(p)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProjectDrillBar({ projectId }: { projectId: number }) {
  const [, setSearchParams] = useSearchParams();
  const projectQuery = useProject(projectId);
  const name = projectQuery.data?.name ?? "…";
  const backToProjects = () => {
    setSearchParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        out.delete("projectId");
        out.delete("building");
        out.set("group", "project");
        out.delete("page");
        return out;
      },
      { replace: true },
    );
  };
  return (
    <div className="flex flex-wrap items-center gap-3">
      <button
        type="button"
        onClick={backToProjects}
        className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-[14px] font-medium hover:bg-muted"
      >
        <ChevronLeft aria-hidden="true" className="size-3.5" />
        Tất cả dự án
      </button>
      <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-[12px] font-medium text-primary">
        <Building2 aria-hidden="true" className="size-3.5" />
        Dự án: {name}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Results: grid / table
// ---------------------------------------------------------------------------

function PropertyGrid({ items }: { items: readonly Property[] }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((property) => (
        <PropertyCard key={property.id} property={property} />
      ))}
    </div>
  );
}

function PropertyGridSkeleton() {
  return (
    <div
      aria-busy="true"
      aria-label="Đang tải danh sách"
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <Card
          key={i}
          className="flex flex-row gap-0 overflow-hidden p-0 sm:flex-col"
        >
          <Skeleton className="aspect-square w-28 shrink-0 rounded-none sm:aspect-[16/9] sm:w-full" />
          <div className="flex flex-1 flex-col gap-2 p-3 sm:gap-3 sm:p-4">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-3 w-full" />
          </div>
        </Card>
      ))}
    </div>
  );
}

function PropertyTableSkeleton() {
  return (
    <div
      aria-busy="true"
      aria-label="Đang tải danh sách"
      className="rounded-xl border border-border bg-card p-2"
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="my-2 h-10 w-full" />
      ))}
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <EmptyState
      title="Không thể tải danh sách bất động sản"
      description="Vui lòng kiểm tra kết nối mạng và thử lại."
      action={
        <Button onClick={onRetry} variant="outline">
          Thử lại
        </Button>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Derivations
// ---------------------------------------------------------------------------

interface StatusStats {
  AVAILABLE: number;
  DEPOSIT: number;
  SOLD: number;
  RENTED: number;
  SALE: number;
  RENT: number;
  byWard: Record<string, number>;
}

function buildStatusStats(items: readonly Property[]): StatusStats {
  const stats: StatusStats = {
    AVAILABLE: 0,
    DEPOSIT: 0,
    SOLD: 0,
    RENTED: 0,
    SALE: 0,
    RENT: 0,
    byWard: {},
  };
  for (const p of items) {
    // Per-status counter — switch covers the four known statuses with
    // typed access so we don't fight exactOptionalPropertyTypes via a
    // Record<string, number> cast.
    switch (p.status) {
      case "AVAILABLE": stats.AVAILABLE += 1; break;
      case "DEPOSIT":   stats.DEPOSIT += 1; break;
      case "SOLD":      stats.SOLD += 1; break;
      case "RENTED":    stats.RENTED += 1; break;
    }
    switch (p.listingType) {
      case "SALE": stats.SALE += 1; break;
      case "RENT": stats.RENT += 1; break;
    }
    if (p.wardCode) {
      stats.byWard[p.wardCode] = (stats.byWard[p.wardCode] ?? 0) + 1;
    }
  }
  return stats;
}

function formatStatsLine(stats: StatusStats): string {
  const parts = [
    stats.AVAILABLE ? `${stats.AVAILABLE} đang bán` : null,
    stats.DEPOSIT ? `${stats.DEPOSIT} đã cọc` : null,
    stats.SOLD + stats.RENTED
      ? `${stats.SOLD + stats.RENTED} đã hoàn tất`
      : null,
  ];
  return parts.filter((s): s is string => s !== null).join(" · ");
}

// ---------------------------------------------------------------------------
// Client-side filtering
// ---------------------------------------------------------------------------

function filterClientSide(
  items: readonly Property[],
  filters: ReturnType<typeof readFilterDraft>,
  staleOnly: boolean,
  staleDays: number,
): Property[] {
  if (
    filters.listingType === undefined &&
    filters.bedrooms === undefined &&
    filters.bathrooms === undefined &&
    filters.areaMin === undefined &&
    filters.areaMax === undefined &&
    filters.priceMin === undefined &&
    filters.priceMax === undefined &&
    !staleOnly
  ) {
    // No client-side narrowing needed.
    return items.slice();
  }
  return items.filter((p) => {
    if (filters.listingType !== undefined && p.listingType !== filters.listingType) {
      return false;
    }
    if (filters.bedrooms !== undefined && (p.bedrooms ?? 0) !== filters.bedrooms) {
      return false;
    }
    if (filters.bathrooms !== undefined && (p.bathrooms ?? 0) !== filters.bathrooms) {
      return false;
    }
    if (filters.areaMin !== undefined && p.area < filters.areaMin) return false;
    if (filters.areaMax !== undefined && p.area > filters.areaMax) return false;
    if (filters.priceMin !== undefined && p.price < filters.priceMin) return false;
    if (filters.priceMax !== undefined && p.price > filters.priceMax) return false;
    if (staleOnly && !isStale(p.lastFreshnessConfirmedAt, staleDays)) return false;
    return true;
  });
}
